import * as React from "react";

import * as R from "ramda";

import classNames from "classnames";

import connectAiChatSubscription from "../../channels/ai_chat_channel";

import GenLoadingSpinner from "../Gen/LoadingSpinner";
import GenButton from "../Gen/Button";

import Markdown from "react-markdown";

import { replaceCustomFieldWithCustomEntries } from "../../src/activities";
import DocOpenAiMessageBar from "./OpenAiMessageBar";

const MAX_COUNT = 20;

interface DocOpenAiChatProps extends OpenAiChatNode, EveryNodeArgs {
  currentActivityCustomEntries?: Record<string, string>;
}

interface DocOpenAiChatState {

}

export default class DocOpenAiChat extends React.Component<DocOpenAiChatProps, DocOpenAiChatState> {
  state = {
    loading: false,
    messages: [],
    newUserMessage: "",
  };

  private aiChatSubscription?: ReturnType<typeof connectAiChatSubscription>;

  componentDidMount() {
    const {
      messages = [],
      startWithAssistant,
    } = this.props;

    if (startWithAssistant && messages.length === 0) {
      this.submitMessages();
    }
  }

  componentWillUnmount() {
    if(this.aiChatSubscription) {
      this.aiChatSubscription.unsubscribe();
      this.aiChatSubscription;
    }
  }

  submitNewUserMessage() {
    const {
      newUserMessage,
    } = this.state;

    const userMessage = {
      role: "user",
      content: newUserMessage,
    } as const;

    this.setState({
      newUserMessage: "",
    });

    this.submitMessages([userMessage]);
  }

  submitMessages(newMessages: Array<OpenAiMessage>=[]) {
    const {
      uid,
      currentUser,
      messages = [],
      aiInstructions,
      currentActivityUserState,
      currentActivityCustomEntries={},
      editState,
    } = this.props;

    if (editState.isEditor) { return; }

    if (!(currentActivityUserState && "state" in currentActivityUserState)) { return; } // TODO: REMOVE COULD CAUSE PROBLEMS

    let compiledAiInstructions = replaceCustomFieldWithCustomEntries(aiInstructions, currentActivityCustomEntries);

    if(currentActivityUserState) {
      const regex = /\{\{\s*(\w+)\s*\}\}/g;
      const regexMatches = compiledAiInstructions.matchAll(regex);

      for (const match of regexMatches) {
        const fullMatch = match[0];
        const stateKey = match[1];

        const renderedMessages = ((currentActivityUserState.state[stateKey]?.messages?.value as Array<OpenAiMessage> || []))
          .map((message) => `${message.role}: ${message.content}`)
          .join("\n\n");

        compiledAiInstructions = compiledAiInstructions.replace(fullMatch, renderedMessages);
      }
    }

    const introMessage = {
      role: "system",
      content: compiledAiInstructions,
    } as const;

    this.setState({
      loading: true,
    });

    const allMessages = [...messages, ...newMessages];
    if (newMessages.length > 0) {
      currentUser.addState({[uid]: {"messages": allMessages}});
    }

    if (!this.aiChatSubscription) {
      this.aiChatSubscription = connectAiChatSubscription({
        chatId: `${currentUser.id}::${currentActivityUserState?.id}::${uid}`,
        onMessageReceived: this.receiveMessage.bind(this),
        onErrorReceived: this.receiveError.bind(this),
      });
    }

    setTimeout(() => this.aiChatSubscription?.chat([introMessage, ...allMessages]), 1000);
  }

  receiveError(error: string) {
    const {
      uid,
      currentUser,
      messages = [],
    } = this.props;

    let newUserMessage = ""

    const lastMessage = R.last(messages)
    if (lastMessage && lastMessage.role === "user") {
      newUserMessage = lastMessage.content
      currentUser.addState({[uid]: {"messages": R.init(messages)}});
    }

    this.setState({
      loading: false,
      newUserMessage: newUserMessage,
    });

    alert("Temporarily unable to connect to InSitu. Need help? Contact support@reeldx.com");
  }

  receiveMessage(message: OpenAiMessage) {
    const {
      uid,
      currentUser,
      messages = [],
    } = this.props;

    currentUser.addState({[uid]: {"messages": messages.concat(message)}});

    this.setState({
      loading: false,
      newUserMessage: "",
    });
  }

  restartChat() {
    const {
      uid,
      currentUser,
    } = this.props;

    this.setState({
      newUserMessage: "",
    });

    currentUser.addState({[uid]: {"messages": []}});
  }

  onEnterPress(e: React.KeyboardEvent) {
    const {
      inactive,
    } = this.props;

    const {
      loading,
      newUserMessage,
    } = this.state;

    if(e.keyCode == 13 && e.shiftKey == false) {
      e.preventDefault();

      if (!(loading || inactive === 2 || this.userMessageCount() >= MAX_COUNT || newUserMessage.length === 0)) {
        this.submitNewUserMessage();
      }
    }
  }

  displayNameFromAiRole(role: OpenAiRole) {
    const {
      userDisplayName,
      assistantDisplayName,
      currentActivityCustomEntries={},
    } = this.props;

    if (role === "assistant") {
      return assistantDisplayName ? replaceCustomFieldWithCustomEntries(assistantDisplayName, currentActivityCustomEntries) : "Assistant";
    } else {
      return userDisplayName ? replaceCustomFieldWithCustomEntries(userDisplayName, currentActivityCustomEntries) : "YOU";
    }
  }

  displayImageSrcFromAiRole(role: OpenAiRole) {
    const {
      assistantDisplayImage,
      userDisplayImage,
      currentVersion,
      currentActivityCustomEntries={},
    } = this.props;

    if (!("aws_folder_url" in currentVersion)) { return "";}

    if (role === "assistant") {
      return assistantDisplayImage && `${currentVersion.aws_folder_url}/compressed/images/${replaceCustomFieldWithCustomEntries(assistantDisplayImage, currentActivityCustomEntries)}`;
    } else {
      return userDisplayImage && `${currentVersion.aws_folder_url}/compressed/images/${replaceCustomFieldWithCustomEntries(userDisplayImage, currentActivityCustomEntries)}`;
    }
  }

  userMessageCount() {
    const {
      messages = [],
    } = this.props;

    return messages.filter((message) => message.role === "user").length;
  }

  render() {
    const {
      uid,
      messages = [],
      inactive,
      inactiveBoolean,
      editState,
      placeholderText,
      currentVersion
    } = this.props;

    const {
      loading,
      newUserMessage,
    } = this.state;

    const userMessageCount = this.userMessageCount();
    const disableChat = editState.isEditor || loading || inactive === 2 || userMessageCount >= MAX_COUNT;
    const disableSubmitChat = disableChat || newUserMessage.length === 0;

    return (
      <div className={classNames("doc-text-input", {"inactive": inactiveBoolean})}>
        <div>
          { messages.map((message, i) => (
            <DocOpenAiMessageBar
              key={`message-${i}`}
              role={message.role}
              name={this.displayNameFromAiRole(message.role)}
              imageSrc={this.displayImageSrcFromAiRole(message.role)}
              content={
                message.role === "assistant" ?
                  <Markdown>{message.content}</Markdown> :
                  message.content.split(/\n/).map((paragraph, i) => <p key={i}>{paragraph}</p>)
              }
            />
          ))}
          { loading ? (
            <DocOpenAiMessageBar
              role="assistant"
              name={this.displayNameFromAiRole("assistant")}
              imageSrc={this.displayImageSrcFromAiRole("assistant")}
              content={
                <GenLoadingSpinner/>
              }
            />
          ) : (
            <DocOpenAiMessageBar
              role="user"
              name={this.displayNameFromAiRole("user")}
              imageSrc={this.displayImageSrcFromAiRole("user")}
              disabled={disableChat}
              content={(
                <div className="relative">
                  <textarea
                    cols={40}
                    rows={4}
                    value={newUserMessage}
                    onChange={(e) => this.setState({newUserMessage: e.target.value})}
                    onKeyDown={this.onEnterPress.bind(this)}
                    disabled={disableChat}
                    autoFocus={!disableChat}
                    placeholder={disableChat ? "" : (!placeholderText || R.isEmpty(placeholderText) ? "Type a message..." : placeholderText)}
                  />
                  <div className="absolute right-1 bottom-3">
                    <GenButton
                      name={<i className="fa fa-arrow-right" />}
                      highlighted
                      disabled={disableSubmitChat}
                      onClick={() => this.submitNewUserMessage()}
                    />
                  </div>
                </div>
              )}
            />
          )}
          <div className="flex justify-end gap-2 items-center">
            <GenButton
              name="Restart"
              highlighted
              disabled={loading || inactive === 2 || messages.length === 0}
              small
              onClick={() => this.restartChat()}
              confirmMessage="Are you sure you want to restart?"
            />
            {userMessageCount >= MAX_COUNT ? "Maximum Reached" : `${userMessageCount + 1} of ${MAX_COUNT}`}
          </div>
        </div>
        { editState.isEditor && (
          <div style={{position: "absolute", top: 0, right: "-20px"}}>
            <GenButton
              name={<i className="fa fa-cog"/>}
              highlighted
              disabled={!editState.isEditing}
              small
              onClick={() => {
                editState.setEditMetadata(uid);
                editState.setCurrentlyEditing(undefined);
              }}
            />
          </div>
        )}
      </div>
    );
  }
}
